import type { AxiosInstance } from 'axios';
import { createCrudService, type CrudService } from '../core/crud';
import { unwrap, type ObjectResponse } from '../core/envelope';
import type { Quotation, QuotationCreate, QuotationFilter } from '../data/quotation';

/**
 * Customer quotations, plus conversion into a sales order.
 *
 * `/Get` and `/all` return line items — unlike Invoice and PurchaseOrder
 * originally did, where item-less reads made the panel's edit form re-save
 * records with no lines.
 */
export function createQuotationService(client: AxiosInstance) {
  const crud = createCrudService<Quotation, QuotationCreate, QuotationFilter>(
    client,
    '/Quotation',
    'quotationId',
  );

  return {
    ...crud,
    /**
     * Convert an accepted quotation into a sales order; resolves to the new
     * order id.
     *
     * The server rejects a quotation that is already converted, cancelled, or
     * has no usable lines. That guard is server-side deliberately — two orders
     * raised from one quote means shipping and billing twice, which is not
     * something to leave to a disabled button.
     */
    convert: async (quotationId: string): Promise<string> => {
      const res = await client.post<ObjectResponse<string>>(
        '/Quotation/convert',
        {},
        { params: { quotationId } },
      );
      return unwrap(res.data) ?? '';
    },
  };
}

export type QuotationService = CrudService<Quotation, QuotationCreate, QuotationFilter> & {
  convert: (quotationId: string) => Promise<string>;
};
